import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Container,
  Input,
  Label,
  FormGroup,
  Button,
  Table,
} from "reactstrap";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import axiosInstance from "../Request";
import { URL } from "../api";

const BLUE = "#123a7a";
const ACCENT = "#1e4fbf";
const RED = "#d9534f";

const money = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const num = (v) => Number(v || 0).toLocaleString("en-IN");

const KPI = ({ label, value, sub, color }) => (
  <Col lg="3" md="6" className="mb-4">
    <Card className="shadow border-0" style={{ borderTop: `4px solid ${color || BLUE}` }}>
      <CardBody>
        <div className="text-uppercase text-muted small font-weight-bold">{label}</div>
        <div className="h3 font-weight-bold mb-0" style={{ color: BLUE }}>{value}</div>
        {sub && <div className="text-muted small mt-1">{sub}</div>}
      </CardBody>
    </Card>
  </Col>
);

const ChartCard = ({ title, points = 0, children }) => (
  <Col xl="6" className="mb-4">
    <Card className="shadow border-0">
      <CardHeader className="bg-white border-0 py-3">
        <h4 className="mb-0" style={{ color: BLUE }}>{title}</h4>
      </CardHeader>
      {/* Scrolls left-right when there are many days; fits when few. */}
      <CardBody style={{ height: 300, overflowX: "auto", overflowY: "hidden" }}>
        <div style={{ height: "100%", minWidth: "100%", width: points > 12 ? points * 50 : "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        </div>
      </CardBody>
    </Card>
  </Col>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const today = new Date().toISOString().split("T")[0];

  const fetchData = useCallback(async (from, to) => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.fromDate = from;
      if (to) params.toDate = to;
      const res = await axiosInstance.get(URL.DASHBOARD_ANALYTICS, { params });
      setData(res.data || null);
    } catch (e) {
      console.error("Dashboard analytics failed:", e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const kpi = data?.kpi || {};
  const inOutTrend = data?.inOutTrend || [];

  // Partner-wise section has its own date filter, defaulting to today.
  const [partnerBreakdown, setPartnerBreakdown] = useState([]);
  const [partnerLoading, setPartnerLoading] = useState(true);
  const [partnerFrom, setPartnerFrom] = useState(today);
  const [partnerTo, setPartnerTo] = useState(today);

  const fetchPartners = useCallback(async (from, to) => {
    setPartnerLoading(true);
    try {
      const params = {};
      if (from) params.fromDate = from;
      if (to) params.toDate = to;
      const res = await axiosInstance.get(URL.DASHBOARD_PARTNER_BREAKDOWN, { params });
      const d = res.data || {};
      setPartnerBreakdown(Array.isArray(d.partners) ? d.partners : []);
      if (d.fromDate) setPartnerFrom(d.fromDate);
      if (d.toDate) setPartnerTo(d.toDate);
    } catch (e) {
      console.error("Partner breakdown failed:", e);
      setPartnerBreakdown([]);
    } finally {
      setPartnerLoading(false);
    }
  }, []);

  // Default load: no dates → backend picks the latest day with activity (today in prod).
  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  return (
    <>
      <div className="header bg-gradient-info pb-6 pt-5 pt-md-6" />
      <Container className="mt--6" fluid>
        <Card className="shadow border-0 mb-4">
          <CardHeader className="bg-white border-0">
            <h3 className="mb-0" style={{ color: BLUE }}>Dashboard</h3>
          </CardHeader>
        </Card>

        {/* Money movement — two big cards */}
        <Row>
          <Col md="6" className="mb-4">
            <Card className="shadow border-0" style={{ borderLeft: "6px solid #2e7d32" }}>
              <CardBody className="d-flex align-items-center">
                <div className="icon icon-shape bg-gradient-success text-white rounded-circle shadow mr-4"
                  style={{ width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="ni ni-money-coins" style={{ fontSize: 26 }} />
                </div>
                <div>
                  <div className="text-uppercase text-muted small font-weight-bold">Amount In</div>
                  <div className="display-4 font-weight-bold mb-0" style={{ color: "#2e7d32" }}>{money(kpi.dailyAmountIn)}</div>
                  <div className="text-muted small">Credits on {kpi.dailyDate || today}</div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md="6" className="mb-4">
            <Card className="shadow border-0" style={{ borderLeft: "6px solid #d9534f" }}>
              <CardBody className="d-flex align-items-center">
                <div className="icon icon-shape bg-gradient-danger text-white rounded-circle shadow mr-4"
                  style={{ width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="ni ni-send" style={{ fontSize: 26 }} />
                </div>
                <div>
                  <div className="text-uppercase text-muted small font-weight-bold">Amount Out</div>
                  <div className="display-4 font-weight-bold mb-0" style={{ color: "#d9534f" }}>{money(kpi.dailyAmountOut)}</div>
                  <div className="text-muted small">Debits on {kpi.dailyDate || today}</div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Payin / Payout trends (day-wise area charts) */}
        <Row>
          <ChartCard title="Payin Trend" points={inOutTrend.length}>
            <AreaChart data={inOutTrend} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="payinFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#5e72e4" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#5e72e4" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => money(v)} />
              <Area type="monotone" dataKey="amount_in" name="Payin" stroke="#5e72e4"
                strokeWidth={2} fill="url(#payinFill)" dot={{ r: 3, fill: "#5e72e4" }} activeDot={{ r: 5 }} />
            </AreaChart>
          </ChartCard>

          <ChartCard title="Payout Trend" points={inOutTrend.length}>
            <AreaChart data={inOutTrend} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="payoutFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#fb6340" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#fb6340" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => money(v)} />
              <Area type="monotone" dataKey="amount_out" name="Payout" stroke="#fb6340"
                strokeWidth={2} fill="url(#payoutFill)" dot={{ r: 3, fill: "#fb6340" }} activeDot={{ r: 5 }} />
            </AreaChart>
          </ChartCard>
        </Row>

        {/* Partner-wise Payin / Payout (date-range, scrollable) */}
        <Card className="shadow border-0 mb-4">
          <CardHeader className="bg-white border-0 py-3">
            <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12 }}>
              <div>
                <h4 className="mb-0" style={{ color: BLUE }}>Partner-wise Payin / Payout</h4>
                <small className="text-muted">By LSP partner. Only partners with activity in the range are shown.</small>
              </div>
              <div className="d-flex align-items-end" style={{ gap: 8 }}>
                <FormGroup className="mb-0">
                  <Label className="mb-1 small font-weight-bold">From</Label>
                  <Input type="date" bsSize="sm" value={partnerFrom} max={today}
                    onChange={(e) => setPartnerFrom(e.target.value)} />
                </FormGroup>
                <FormGroup className="mb-0">
                  <Label className="mb-1 small font-weight-bold">To</Label>
                  <Input type="date" bsSize="sm" value={partnerTo} max={today}
                    onChange={(e) => setPartnerTo(e.target.value)} />
                </FormGroup>
                <Button color="primary" size="sm" disabled={partnerLoading}
                  onClick={() => fetchPartners(partnerFrom, partnerTo)}>Search</Button>
                <Button color="secondary" size="sm" disabled={partnerLoading}
                  onClick={() => { setPartnerFrom(today); setPartnerTo(today); fetchPartners(today, today); }}>Today</Button>
                <span className="text-muted small ml-1">{partnerBreakdown.length} partners</span>
              </div>
            </div>
          </CardHeader>
          <CardBody className="pt-0">
            <div style={{ maxHeight: 340, overflowY: "auto" }}>
              <Table className="align-items-center table-flush" size="sm">
                <thead className="thead-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr><th>Partner</th><th className="text-right">Payin</th><th className="text-right">Payout</th></tr>
                </thead>
                <tbody>
                  {partnerLoading ? (
                    <tr><td colSpan="3" className="text-center text-muted py-3">Loading…</td></tr>
                  ) : partnerBreakdown.length === 0 ? (
                    <tr><td colSpan="3" className="text-center text-muted py-3">No partner activity in this range.</td></tr>
                  ) : partnerBreakdown.map((p) => (
                    <tr key={p.partner}>
                      <td>{p.partner}</td>
                      <td className="text-right font-weight-bold" style={{ color: "#2e7d32" }}>{money(p.payin)}</td>
                      <td className="text-right font-weight-bold" style={{ color: "#d9534f" }}>{money(p.payout)}</td>
                    </tr>
                  ))}
                </tbody>
                {!partnerLoading && partnerBreakdown.length > 0 && (
                  <tfoot>
                    <tr className="font-weight-bold" style={{ background: "#f6f9fc" }}>
                      <td>Total</td>
                      <td className="text-right" style={{ color: "#2e7d32" }}>{money(partnerBreakdown.reduce((s, p) => s + Number(p.payin || 0), 0))}</td>
                      <td className="text-right" style={{ color: "#d9534f" }}>{money(partnerBreakdown.reduce((s, p) => s + Number(p.payout || 0), 0))}</td>
                    </tr>
                  </tfoot>
                )}
              </Table>
            </div>
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default Dashboard;
